import {
  defaultChainOptions,
  fetchBeacon,
  type Chain,
  type ChainClient,
  type ChainInfo,
  type ChainOptions,
  type RandomnessBeacon,
} from "drand-client";

/**
 * drand quicknet (League of Entropy mainnet, unchained mode, 3 s period).
 * Pinned so that a relay cannot serve a different chain.
 */
export const QUICKNET_CHAIN_HASH =
  "52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971";

export const QUICKNET_CHAIN_INFO: ChainInfo = {
  public_key:
    "83cf0f2896adee7eb8b5f01fcad3912212c437e0073e911fb90022d3e760183c8c4b450b6a0a6c3ac6a5776a2d1064510d1fec758c921cc22b0e17e63aaf4bcb5ed66304de9cf809bd274ca73bab4af5a6e9c76a4bc09e76eae8991ef5ece45a",
  period: 3,
  genesis_time: 1692803367,
  hash: QUICKNET_CHAIN_HASH,
  groupHash: "f477d5c89f21a17c863a7f937c6a6d15859414d2be09cd448d4279af331c5d3e",
  schemeID: "bls-unchained-g1-rfc9380",
  metadata: { beaconID: "quicknet" },
};

/** Public HTTP relays run by League of Entropy members. Tried in order. */
export const QUICKNET_RELAYS = [
  "https://api.drand.sh",
  "https://api2.drand.sh",
  "https://api3.drand.sh",
  "https://drand.cloudflare.com",
];

const chainOptions: ChainOptions = {
  ...defaultChainOptions,
  chainVerificationParams: {
    chainHash: QUICKNET_CHAIN_HASH,
    publicKey: QUICKNET_CHAIN_INFO.public_key,
  },
};

/** First round whose emission time is at or after `unlockAtMs`. Rounds start at 1. */
export function roundForTime(unlockAtMs: number, info: ChainInfo = QUICKNET_CHAIN_INFO): number {
  if (!Number.isFinite(unlockAtMs)) throw new Error("unlockAtMs must be a finite number");
  const elapsedSeconds = unlockAtMs / 1000 - info.genesis_time;
  if (elapsedSeconds <= 0) return 1;
  const round = Math.ceil(elapsedSeconds / info.period) + 1;
  if (!Number.isSafeInteger(round)) throw new Error("round is not a safe integer");
  return round;
}

/** Emission time (ms since epoch) of a round. Actual publication may lag by a few seconds. */
export function timeForRound(round: number, info: ChainInfo = QUICKNET_CHAIN_INFO): number {
  if (!Number.isSafeInteger(round) || round < 1) throw new Error("round must be a positive integer");
  return (info.genesis_time + (round - 1) * info.period) * 1000;
}

/** Latest round that should already exist at `nowMs`. */
export function currentRound(nowMs: number = Date.now(), info: ChainInfo = QUICKNET_CHAIN_INFO): number {
  const elapsedSeconds = nowMs / 1000 - info.genesis_time;
  if (elapsedSeconds < 0) return 0;
  return Math.floor(elapsedSeconds / info.period) + 1;
}

/** Per-relay limit (PLAN.md 4.11 H9): a relay that hangs must not block the next one. */
export const RELAY_TIMEOUT_MS = 5000;

export type RelayOptions = {
  /** Default RELAY_TIMEOUT_MS. */
  timeoutMs?: number;
};

const pinnedChain = (baseUrl: string): Chain => ({ baseUrl, info: async () => QUICKNET_CHAIN_INFO });

/**
 * Verified beacons by round, shared by every client of this page or process. Bounded, least
 * recently used first out, so a tab or an opener left open for days does not grow without end.
 */
export const MAX_CACHED_BEACONS = 512;
const verifiedBeacons = new Map<number, RandomnessBeacon>();
/** Reads of a round in progress: a second reader of the same round waits for the first. */
const pendingBeacons = new Map<number, Promise<RandomnessBeacon>>();

function rememberBeacon(round: number, beacon: RandomnessBeacon): void {
  verifiedBeacons.delete(round);
  verifiedBeacons.set(round, beacon);
  while (verifiedBeacons.size > MAX_CACHED_BEACONS) verifiedBeacons.delete(verifiedBeacons.keys().next().value!);
}

function cachedBeacon(round: number): RandomnessBeacon | undefined {
  const beacon = verifiedBeacons.get(round);
  if (beacon) rememberBeacon(round, beacon);
  return beacon;
}

/** Tests only: forget verified beacons. */
export function clearBeaconCache(): void {
  verifiedBeacons.clear();
  pendingBeacons.clear();
}

/** Tests only: how many verified beacons are kept. */
export function cachedBeaconCount(): number {
  return verifiedBeacons.size;
}

/** Tests only: put a beacon into the cache as if a relay had given it and it had passed verification. */
export function rememberBeaconForTests(beacon: RandomnessBeacon): void {
  rememberBeacon(beacon.round, beacon);
}

/** A ChainClient over one beacon source whose results fetchBeacon verifies against the pinned chain. */
function verifyingClient(baseUrl: string, get: (round: number) => Promise<RandomnessBeacon>): ChainClient {
  return {
    options: chainOptions,
    chain: () => pinnedChain(baseUrl),
    get,
    latest: () => Promise.reject(new Error("not supported")),
  };
}

/**
 * Verifies a beacon against the pinned quicknet public key and the expected round. Used for
 * every relay answer and for a beacon stored in a downloaded capsule file (section 4.8).
 */
export async function verifyQuicknetBeacon(beacon: RandomnessBeacon, round: number): Promise<RandomnessBeacon> {
  return fetchBeacon(verifyingClient("stored://quicknet", async () => beacon), round);
}

/** Runs `run` with a signal that is aborted after `ms`, so a relay that hangs does not keep its request open. */
function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>, ms: number, what: string): Promise<T> {
  const controller = new AbortController();
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${what}: no answer within ${ms} ms`));
      controller.abort();
    }, ms);
    run(controller.signal).then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

/** One relay answer as JSON; the error wording keeps the status, which looksLikeNotYet reads. */
async function fetchRelayJson(url: string, signal: AbortSignal): Promise<RandomnessBeacon> {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Error response fetching ${url} - got ${response.status}`);
  return (await response.json()) as RandomnessBeacon;
}

const looksLikeNotYet = (err: unknown) => /404|not.*(found|exist|yet)|too early/i.test(err instanceof Error ? err.message : String(err));

/**
 * A ChainClient over several relays (PLAN.md 4.11 H9). One attempt = fetch from one relay AND
 * verify the beacon; a relay that fails, answers with a bad beacon or takes longer than the
 * timeout is skipped for the next one. Chain info is pinned, so encryption needs no network.
 */
class FailoverChainClient implements ChainClient {
  options: ChainOptions = chainOptions;
  private readonly relays: { url: string; base: string }[];
  private readonly timeoutMs: number;

  constructor(relayUrls: string[], opts: RelayOptions) {
    if (relayUrls.length === 0) throw new Error("at least one relay URL is required");
    this.timeoutMs = opts.timeoutMs ?? RELAY_TIMEOUT_MS;
    this.relays = relayUrls.map((url) => ({ url, base: `${url}/${QUICKNET_CHAIN_HASH}` }));
  }

  chain(): Chain {
    return pinnedChain(`${this.relays[0].url}/${QUICKNET_CHAIN_HASH}`);
  }

  async get(roundNumber: number): Promise<RandomnessBeacon> {
    const cached = cachedBeacon(roundNumber);
    if (cached) return cached;
    const pending = pendingBeacons.get(roundNumber);
    if (pending) return pending;
    const read = this.read(roundNumber).finally(() => pendingBeacons.delete(roundNumber));
    pendingBeacons.set(roundNumber, read);
    return read;
  }

  private async read(roundNumber: number): Promise<RandomnessBeacon> {
    let notYet = false;
    const errors: string[] = [];
    for (const relay of this.relays) {
      try {
        const beacon = await withTimeout(
          (signal) => fetchBeacon(verifyingClient(relay.base, (round) => fetchRelayJson(`${relay.base}/public/${round}`, signal)), roundNumber),
          this.timeoutMs,
          relay.url,
        );
        rememberBeacon(roundNumber, beacon);
        return beacon;
      } catch (err) {
        if (looksLikeNotYet(err)) notYet = true;
        errors.push(`${relay.url}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    // Wording the capsule code maps to ROUND_NOT_REACHED when any relay said "not yet".
    if (notYet) throw new Error(`round ${roundNumber} not found (404) on the relays that answered`);
    throw new Error(`no relay gave a valid beacon for round ${roundNumber}: ${errors.join("; ")}`);
  }

  /** Newest beacon, verified like every other (used by tests and the soak script). */
  async latest(): Promise<RandomnessBeacon> {
    const errors: string[] = [];
    for (const relay of this.relays) {
      try {
        const beacon = await withTimeout((signal) => fetchRelayJson(`${relay.base}/public/latest`, signal), this.timeoutMs, relay.url);
        return await verifyQuicknetBeacon(beacon, beacon.round);
      } catch (err) {
        errors.push(`${relay.url}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    throw new Error(`no relay gave a valid latest beacon: ${errors.join("; ")}`);
  }
}

export function createQuicknetClient(relayUrls: string[] = QUICKNET_RELAYS, opts: RelayOptions = {}): ChainClient {
  return new FailoverChainClient(relayUrls, opts);
}

/**
 * Client for a downloaded capsule whose file carries the beacon of its round (section 4.8): the
 * stored beacon is verified first; without a valid one it falls back to `fallback` (the relays),
 * or reports the round as not available.
 */
export function createStoredBeaconClient(stored: RandomnessBeacon, fallback: ChainClient | null = null): ChainClient {
  return {
    options: chainOptions,
    chain: () => pinnedChain("stored://quicknet"),
    async get(roundNumber: number) {
      if (stored.round === roundNumber) {
        try {
          return await verifyQuicknetBeacon(stored, roundNumber);
        } catch {
          // A damaged or forged stored beacon is ignored, never trusted.
        }
      }
      if (fallback) return fallback.get(roundNumber);
      throw new Error(`round ${roundNumber} not found (404): no valid stored beacon and no network`);
    },
    latest: () => Promise.reject(new Error("not supported")),
  };
}

/**
 * Offline client for tests: serves beacons from a fixed map and never touches the network.
 */
export function createFixtureClient(beacons: Record<number, RandomnessBeacon>): ChainClient {
  return {
    options: chainOptions,
    chain: () => ({ baseUrl: "fixture://quicknet", info: async () => QUICKNET_CHAIN_INFO }),
    async get(roundNumber: number) {
      const beacon = beacons[roundNumber];
      // Same wording a relay uses for a round that does not exist yet (HTTP 404).
      if (!beacon) throw new Error(`fixture: round ${roundNumber} not found (404)`);
      return beacon;
    },
    async latest() {
      const rounds = Object.keys(beacons).map(Number);
      if (rounds.length === 0) throw new Error("fixture has no beacons");
      return beacons[Math.max(...rounds)];
    },
  };
}
